import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isKurumsalOpen, setIsKurumsalOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsKurumsalOpen(false);
  }, [location]);

  const navLinks = [
    { name: 'Anasayfa', path: '/' },
    { 
      name: 'Kurumsal', 
      path: '#',
      dropdown: [
        { name: 'Hakkımızda', path: '/about' },
        { name: 'Vizyon-Misyon', path: '/vision-mission' },
      ]
    },
    { name: 'Ürünler', path: '/products' },
    { name: 'Üretim & Kalite', path: '/production-quality' },
    { name: 'Sürdürülebilirlik', path: '/sustainability' },
    { name: 'İnsan Kaynakları', path: '/human-resources' },
    { name: 'İletişim', path: '/contact' },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center cursor-pointer z-50">
            <img 
              src="https://static.readdy.ai/image/07f91bd9f2ad3b84149b39a4e0627797/74f21b04c4458e8ad7cedf8e00c137c7.png" 
              alt="AnadoluPet Logo" 
              className="h-10 sm:h-12 w-auto"
            />
          </Link>

          {/* Desktop Menu */}
          <div className="hidden xl:flex items-center gap-6 2xl:gap-8">
            {navLinks.map((link) => (
              link.dropdown ? (
                <div 
                  key={link.name}
                  className="relative group"
                  onMouseEnter={() => setIsKurumsalOpen(true)}
                  onMouseLeave={() => setIsKurumsalOpen(false)}
                >
                  <button
                    className={`relative text-sm font-medium transition-colors duration-300 whitespace-nowrap text-navy hover:text-green cursor-pointer flex items-center gap-1 ${
                      location.pathname === '/about' || location.pathname === '/vision-mission' ? 'text-green' : ''
                    }`}
                  >
                    {link.name}
                    <i className={`ri-arrow-down-s-line text-base transition-transform duration-300 ${isKurumsalOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  <AnimatePresence>
                    {isKurumsalOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.2 }}
                        className="absolute top-full left-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-100 py-2 z-50"
                      >
                        {link.dropdown.map((item) => (
                          <Link
                            key={item.path}
                            to={item.path}
                            className={`block px-4 py-2.5 text-sm font-medium transition-colors duration-200 cursor-pointer ${
                              location.pathname === item.path
                                ? 'bg-green/10 text-green'
                                : 'text-navy hover:bg-gray-50 hover:text-green'
                            }`}
                          >
                            {item.name}
                          </Link>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`relative text-sm font-medium transition-colors duration-300 whitespace-nowrap group text-navy hover:text-green cursor-pointer ${
                    location.pathname === link.path ? 'text-green' : ''
                  }`}
                >
                  {link.name}
                  <span
                    className={`absolute bottom-0 left-0 w-0 h-0.5 bg-green transition-all duration-300 group-hover:w-full ${
                      location.pathname === link.path ? 'w-full' : ''
                    }`}
                  ></span>
                </Link>
              )
            ))}
            
            <Link
              to="/quote"
              className="bg-green hover:bg-green/90 text-white px-5 2xl:px-6 py-2 2xl:py-2.5 rounded-lg font-semibold text-sm transition-all duration-300 transform hover:scale-105 whitespace-nowrap cursor-pointer shadow-lg shadow-green/20"
            >
              Teklif Al
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="xl:hidden w-10 h-10 flex items-center justify-center text-navy hover:text-green transition-colors z-50 relative"
            aria-label="Menu"
          >
            <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`} />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="xl:hidden bg-white border-t border-gray-100 shadow-lg"
          >
            <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-4 space-y-2">
              {navLinks.map((link) => (
                link.dropdown ? (
                  <div key={link.name}>
                    <button
                      onClick={() => setIsKurumsalOpen(!isKurumsalOpen)}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-lg text-sm font-medium transition-colors duration-300 cursor-pointer ${
                        location.pathname === '/about' || location.pathname === '/vision-mission'
                          ? 'bg-green/10 text-green'
                          : 'text-navy hover:bg-gray-50 hover:text-green'
                      }`}
                    >
                      {link.name}
                      <i className={`ri-arrow-down-s-line text-base transition-transform duration-300 ${isKurumsalOpen ? 'rotate-180' : ''}`} />
                    </button>
                    <AnimatePresence>
                      {isKurumsalOpen && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.2 }}
                          className="ml-4 mt-1 space-y-1"
                        >
                          {link.dropdown.map((item) => (
                            <Link
                              key={item.path}
                              to={item.path}
                              className={`block px-4 py-2.5 rounded-lg text-sm font-medium transition-colors duration-300 cursor-pointer ${
                                location.pathname === item.path
                                  ? 'bg-green/10 text-green'
                                  : 'text-navy hover:bg-gray-50 hover:text-green'
                              }`}
                            >
                              {item.name}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`block px-4 py-3 rounded-lg text-sm font-medium transition-colors duration-300 cursor-pointer ${
                      location.pathname === link.path
                        ? 'bg-green/10 text-green'
                        : 'text-navy hover:bg-gray-50 hover:text-green'
                    }`}
                  >
                    {link.name}
                  </Link>
                )
              ))}
              
              <Link
                to="/quote"
                className="block text-center bg-green hover:bg-green/90 text-white px-6 py-3 rounded-lg font-semibold text-sm transition-all duration-300 cursor-pointer shadow-lg shadow-green/20 mt-4"
              >
                Teklif Al
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
